import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import {
  CACHE_KEYS,
  TaxationEstimate,
  TaxationEstimateList,
} from "@rentablo/api/apiv1";
import { toCurrency } from "@helpers";
import { RHFSelect } from "@rentablo/components";
import { SimpleTable } from "@rentablo/components";
import { useUpdateTaxationCategory } from "@features/cashback/components/CashbackSummary/TaxHelper/useTaxEstimate";

const CategorySelect = ({
  taxationEstimate,
  onValueChange,
}: {
  taxationEstimate: TaxationEstimate;
  onValueChange: (value: TaxationEstimate) => void;
}) => {
  const { t } = useTranslation();
  const { control } = useForm({
    defaultValues: {
      exemptionSelect: taxationEstimate.taxationCategory,
    },
    mode: "onChange",
  });
  const newValue = useWatch({ control, name: "exemptionSelect" });

  useEffect(() => {
    if (newValue !== taxationEstimate.taxationCategory) {
      onValueChange({
        ...taxationEstimate,
        taxationCategory: newValue,
      });
    }
  }, [newValue, onValueChange, taxationEstimate]);

  return (
    <RHFSelect
      name="exemptionSelect"
      control={control}
      options={t("domains.cashback.dashboard.taxHelper.categories", {
        returnObjects: true,
      })}
    />
  );
};

type Props = {
  data?: TaxationEstimateList;
};

export const TaxationProducts = ({ data: taxationData }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const { mutate: updateTaxationCategory } = useUpdateTaxationCategory();

  const onTaxationCategoryChange = useCallback(
    (data: TaxationEstimate) => {
      updateTaxationCategory(
        {
          updateTaxationEstimateParams: data,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(
              t("domains.cashback.dashboard.taxHelper.products.success"),
              {
                variant: "success",
                autoHideDuration: 3000,
              }
            );
            queryClient.refetchQueries({
              queryKey: [CACHE_KEYS.GET_TAXATION_ESTIMATE],
            });
          },
        }
      );
    },
    [updateTaxationCategory, enqueueSnackbar, queryClient, t]
  );

  const data = useMemo(() => {
    return (
      taxationData?.taxationEstimates?.map((taxationEstimate) => ({
        name: {
          label: t("domains.cashback.dashboard.taxHelper.products.name"),
          value: taxationEstimate.name,
        },
        category: {
          label: t("domains.cashback.dashboard.taxHelper.products.category"),
          value: (
            <CategorySelect
              taxationEstimate={taxationEstimate}
              onValueChange={onTaxationCategoryChange}
            />
          ),
        },
        taxableAmount: {
          label: t(
            "domains.cashback.dashboard.taxHelper.products.taxableAmount"
          ),
          value: toCurrency(taxationEstimate.taxationRelevantAmount, "EUR"),
        },
      })) || []
    );
  }, [taxationData?.taxationEstimates, onTaxationCategoryChange, t]);

  const mobileData = useMemo(() => {
    return (
      taxationData?.taxationEstimates?.map((taxationEstimate) => [
        {
          allData: {
            label: taxationEstimate.name!,
            value: (
              <Box>
                <Box mb={4}>
                  <CategorySelect
                    taxationEstimate={taxationEstimate}
                    onValueChange={onTaxationCategoryChange}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    fontWeight={600}
                  >
                    {t(
                      "domains.cashback.dashboard.taxHelper.products.taxableAmount"
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.success"
                    fontWeight={600}
                  >
                    {toCurrency(taxationEstimate.taxationRelevantAmount, "EUR")}
                  </Typography>
                </Box>
              </Box>
            ),
          },
        },
      ]) || []
    );
  }, [taxationData?.taxationEstimates, onTaxationCategoryChange, t]);

  if (!data) {
    return null;
  }

  if (isLessMd) {
    return (
      <Box>
        {mobileData.map((item, index) => (
          <Box mb={6}>
            <SimpleTable data={item} key={index} breakpoint="md" />
          </Box>
        ))}
      </Box>
    );
  }

  return <SimpleTable data={data} />;
};
